<template>
	<EventTeamLayout :event="event" :subtitle="trans('events.teams.participants.title')" :teams="teams" :team-count="teamCount" :can-create="canCreate" @created="loadTeams">
		<Empty v-if="teams.length === 0"/>
		<template v-for="(team, teamIndex) in teams" :key="`team-${team.id ?? teamIndex}-${team.name}`">
			<div class="p-4 flex items-end gap-3">
				<LabeledObject full>
					<template #label>{{ trans('events.teams.number', {number: (teamIndex + 1).toString()}) }}</template>
					<input v-model="team.name" type="text" maxlength="255">
				</LabeledObject>
				<PrimaryButton :color="Color.Green" @click.stop="updateTeam(team)">
					<i class="fa-solid fa-check"/>
				</PrimaryButton>
				<div class="input-like">
					{{ team.competitors.length }}
					<i class="fa-solid fa-users"/>
				</div>
				<div class="input-like">{{ transChoice('events.teams.scores.score', team.score) }}</div>
			</div>
			<div class="w-full overflow-x-auto">
				<table class="bg-gray-50 dark:bg-gray-900 text-nowrap whitespace-nowrap">
					<thead>
						<tr>
							<th class="px-3 py-2">{{ trans('events.teams.competitor') }}</th>
							<th class="px-3 py-2">{{ trans('events.teams.gender') }}</th>
							<th class="px-3 py-2">{{ trans('events.teams.age') }}</th>
							<th class="px-3 py-2">{{ trans('events.teams.major') }}</th>
							<th class="px-3 py-2">{{ trans('events.teams.college') }}</th>
							<th class="px-3 py-2">{{ trans('events.teams.location') }}</th>
							<th class="px-3 py-2">{{ trans('events.teams.credits_obtained') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(competitor, competitorIndex) in team.competitors" :class="`${competitor.gender === Gender.Male ? 'bg-blue-50 dark:bg-blue-950' : (competitor.gender === Gender.Female ? 'bg-pink-50 dark:bg-pink-950' : 'bg-violet-50 dark:bg-violet-950')}`">
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700">
								<div class="flex items-center gap-3">
									<template v-if="canCreate">
										<i v-if="competitorIsSelected(teamIndex, competitorIndex)" class="fa-solid fa-square-check cursor-pointer" @click.stop="deselectCompetitor"/>
										<i v-else-if="readyToSwap(teamIndex)" class="fa-regular fa-arrow-right-arrow-left cursor-pointer" @click.stop="swapCompetitor(teamIndex, competitorIndex)"/>
										<i v-else-if="selectedCompetitor.teamIndex === null" class="fa-regular fa-square cursor-pointer" @click.stop="selectCompetitor(teamIndex, competitorIndex)"/>
										<i v-else class="fa-solid fa-ban"/>
									</template>
									<p>{{ competitor.full_name }}</p>
								</div>
							</td>
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700 text-center">
								<i v-if="competitor.gender === Gender.Male" class="fa-solid fa-mars"/>
								<i v-if="competitor.gender === Gender.Female" class="fa-solid fa-venus"/>
								<i v-if="competitor.gender === Gender.Other" class="fa-solid fa-venus-mars"/>
							</td>
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700 text-center">{{ competitor.age }}</td>
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700">{{ competitor.college_programs.length > 0 ? competitor.college_programs[0].major.name : trans('general.n_a') }}</td>
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700">{{ competitor.college_programs.length > 0 ? competitor.college_programs[0].college.name : trans('general.n_a') }}</td>
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700">{{ competitor.location.name }}</td>
							<td class="px-3 py-2 border-y border-gray-200 dark:border-gray-700 text-center">{{ competitor.college_programs.length > 0 ? competitor.college_programs[0].credits_obtained : trans('general.n_a') }}</td>
						</tr>
					</tbody>
					<tfoot>
						<tr>
							<th class="px-3 py-2"/>
							<th class="px-3 py-2">{{ getTeamMaleCount(team) }} - {{ getTeamFemaleCount(team) }} - {{ getTeamOtherCount(team) }}</th>
							<th class="px-3 py-2">{{ getTeamAgeAverage(team) }}</th>
							<th class="px-3 py-2"/>
							<th class="px-3 py-2"/>
							<th class="px-3 py-2"/>
							<th class="px-3 py-2">{{ getTeamCreditsAverage(team) }}</th>
						</tr>
					</tfoot>
				</table>
			</div>
		</template>
	</EventTeamLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {Gender} from '@/Types/Enums/Gender'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import {type Team} from '@/Types/Events/Teams/Team'
import {trans, transChoice} from 'laravel-vue-i18n'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import EventTeamLayout from '@/Layouts/EventTeamLayout.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

interface SelectedCompetitor {
	teamIndex?:number,
	competitorIndex?:number,
}

const props = defineProps<{
	event:Event,
	teams:Array<Team>,
	teamCount:number,
	canCreate:boolean,
}>()

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const teams:Ref<Array<Team>> = ref(props.teams)
const teamCount:Ref<number> = ref(props.teamCount)
const canCreate:Ref<boolean> = ref(props.canCreate)
const selectedCompetitor:Ref<SelectedCompetitor> = ref({
	teamIndex: undefined,
	competitorIndex: undefined,
})

function loadTeams(newTeams:Array<Team>):void{
	teams.value = newTeams
	teamCount.value = newTeams.length
}

function getTeamMaleCount(team:Team):number{
	let count = 0
	team.competitors.forEach((competitor:User) => {
		if(competitor.gender === 'male'){
			count += 1
		}
	})
	return count
}

function getTeamFemaleCount(team:Team):number{
	let count = 0
	team.competitors.forEach((competitor:User) => {
		if(competitor.gender === 'female'){
			count += 1
		}
	})
	return count
}

function getTeamOtherCount(team:Team):number{
	let count = 0
	team.competitors.forEach((competitor:User) => {
		if(competitor.gender === 'other'){
			count += 1
		}
	})
	return count
}

function getTeamAgeAverage(team:Team):string{
	let count = 0
	team.competitors.forEach((competitor:User) => {
		count += competitor.age
	})
	return (count / team.competitors.length).toFixed(2)
}

function getTeamCreditsAverage(team:Team):string{
	let count = 0
	let total = 0
	team.competitors.forEach((competitor:User) => {
		if(competitor.college_programs.length > 0){
			count += competitor.college_programs[0].credits_obtained
			total += 1
		}
	})
	return (total > 0 ? count / total : 0).toFixed(2)
}

function competitorIsSelected(teamIndex:number, competitorIndex:number):boolean{
	return selectedCompetitor.value.teamIndex === teamIndex && selectedCompetitor.value.competitorIndex === competitorIndex
}

function readyToSwap(teamIndex:number):boolean{
	return selectedCompetitor.value.teamIndex !== null && selectedCompetitor.value.teamIndex !== teamIndex
}

function swapCompetitor(competitor2TeamIndex:number, competitor2Index:number):void{
	if(selectedCompetitor.value.teamIndex && selectedCompetitor.value.competitorIndex){
		const competitor1TeamIndex:number = selectedCompetitor.value.teamIndex
		const competitor1Index:number = selectedCompetitor.value.competitorIndex
		const team1 = teams.value[competitor1TeamIndex]
		const team2 = teams.value[competitor2TeamIndex]
		const competitor1 = team1.competitors[competitor1Index]
		const competitor2 = team2.competitors[competitor2Index]
		const confirmed = confirm(trans('events.teams.swap_competitor', {
			team1: team1.name,
			competitor1: competitor1.full_name,
			team2: team2.name,
			competitor2: competitor2.full_name,
		}))
		if(confirmed){
			teams.value[competitor1TeamIndex].competitors.splice(competitor1Index, 1, competitor2)
			teams.value[competitor2TeamIndex].competitors.splice(competitor2Index, 1, competitor1)
			deselectCompetitor()
		}
	}
}

function selectCompetitor(teamIndex:number, competitorIndex:number):void{
	selectedCompetitor.value.teamIndex = teamIndex
	selectedCompetitor.value.competitorIndex = competitorIndex
}

function deselectCompetitor():void{
	selectedCompetitor.value.teamIndex = undefined
	selectedCompetitor.value.competitorIndex = undefined
}

function updateTeam(team:Team):void{
	if(!canCreate.value && !working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.teams.update', {
			eventId: event.value.id,
			team: team.id,
		}), team).then(({data}) => {
			event.value = data
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>